import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Upload, Users, CheckCircle2, XCircle, AlertCircle, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Three-step bulk grant flow:
 *   step="upload"   — pick CSV / paste emails
 *   step="preview"  — show classified rows + confirm
 *   step="running"  — poll job progress
 *
 * Hidden behind a button in the Attendees tab. Refreshes the parent list on
 * close once any rows were granted.
 */
export default function BulkGrantDialog({ open, onOpenChange, onComplete }) {
  const [step, setStep] = useState("upload");
  const [rawRows, setRawRows] = useState([]); // parsed input rows
  const [preview, setPreview] = useState(null); // {rows, summary}
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setStep("upload");
      setRawRows([]);
      setPreview(null);
      setBusy(false);
      setJobId(null);
      setJobStatus(null);
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [open]);

  const handleFile = async (file) => {
    if (!file) return;
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (!rows.length) {
        toast.error("Couldn't find any rows in that CSV.");
        return;
      }
      setRawRows(rows);
      toast.success(`Parsed ${rows.length} row${rows.length === 1 ? "" : "s"} from ${file.name}`);
    } catch (e) {
      toast.error("Couldn't read that file: " + (e?.message || "unknown error"));
    }
  };

  const handlePaste = (text) => {
    const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    // If the first line looks like a header (contains the word "email"),
    // skip it so the parsed-count matches what the preview API will actually
    // grant.
    const first = (lines[0] || "").toLowerCase();
    const dataLines = /email/.test(first) ? lines.slice(1) : lines;
    const rows = dataLines.map((line) => {
      const parts = line.split(/[,\t]/).map((p) => p.trim().replace(/^"|"$/g, ""));
      return { email: parts[0] || "", first_name: parts[1] || "", last_name: parts[2] || "" };
    });
    setRawRows(rows);
    toast.success(`Parsed ${rows.length} row${rows.length === 1 ? "" : "s"} from pasted text`);
  };

  const runPreview = async () => {
    if (!rawRows.length) {
      toast.error("Upload a CSV or paste a list first.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-grants/preview", { rows: rawRows });
      setPreview(r.data);
      setStep("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  const confirmGrant = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/bulk-grants", { rows: rawRows });
      setJobId(r.data.job_id);
      setStep("running");
      pollRef.current = setInterval(async () => {
        try {
          const jr = await api.get(`/admin/bulk-grants/${r.data.job_id}`);
          setJobStatus(jr.data);
          if (jr.data.status === "done" || jr.data.status === "failed") {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        } catch {/* keep polling on transient errors */}
      }, 1500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not start job");
    } finally {
      setBusy(false);
    }
  };

  const closeAndRefresh = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    const grantedSome = jobStatus?.granted > 0;
    onOpenChange(false);
    if (grantedSome && onComplete) onComplete();
  };

  const summary = preview?.summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden" data-testid="bulk-grant-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
          <DialogTitle className="font-heading tracking-tight text-2xl">
            Bulk-grant replay access
          </DialogTitle>
          <DialogDescription>
            Upload a CSV (or paste a list) of in-person attendees. Each new
            account gets a welcome email with a temp password and a link to
            their replay portal. Existing ticket holders are skipped — no
            duplicates, no spam.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-2">
          {step === "upload" && (
            <UploadStep
              rawRows={rawRows}
              onFile={handleFile}
              onPaste={handlePaste}
              onReset={() => setRawRows([])}
            />
          )}

          {step === "preview" && preview && (
            <PreviewStep preview={preview} />
          )}

          {step === "running" && (
            <RunningStep status={jobStatus} totalActionable={summary?.will_grant || 0} />
          )}
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
          {step === "upload" && (
            <Button
              onClick={runPreview}
              disabled={busy || !rawRows.length}
              className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
              data-testid="bulk-grant-preview-btn"
            >
              {busy ? "Checking…" : `Preview ${rawRows.length} row${rawRows.length === 1 ? "" : "s"}`}
            </Button>
          )}
          {step === "preview" && (
            <div className="flex items-center gap-2 ml-auto">
              <Button
                variant="outline"
                onClick={() => setStep("upload")}
                className="rounded-full"
                data-testid="bulk-grant-back-btn"
              >
                Back
              </Button>
              <Button
                onClick={confirmGrant}
                disabled={busy || !summary?.will_grant}
                className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
                data-testid="bulk-grant-confirm-btn"
              >
                {busy ? "Starting…"
                  : summary?.will_grant
                    ? `Grant & email ${summary.will_grant} attendees`
                    : "Nothing to grant"}
              </Button>
            </div>
          )}
          {step === "running" && (
            <Button
              onClick={closeAndRefresh}
              disabled={jobStatus?.status !== "done" && jobStatus?.status !== "failed"}
              className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white"
              data-testid="bulk-grant-close-btn"
            >
              {jobStatus?.status === "done" ? "All done — close"
                : jobStatus?.status === "failed" ? "Close"
                  : "Working…"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UploadStep({ rawRows, onFile, onPaste, onReset }) {
  const [pasteText, setPasteText] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);
  const inputRef = useRef(null);

  const sendTestEmail = async () => {
    const em = testEmail.trim();
    if (!em) { toast.error("Enter an email to send the test to."); return; }
    setSendingTest(true);
    try {
      await api.post("/admin/bulk-grants/test-email", { email: em });
      toast.success(`Test email sent to ${em}. Check your inbox (and spam folder).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Test email failed");
    } finally {
      setSendingTest(false);
    }
  };

  return (
    <div className="space-y-5 pt-2" data-testid="bulk-grant-upload-step">
      <div
        className="rounded-2xl border-2 border-dashed border-slate-300 hover:border-[#28BBE3] hover:bg-[#28BBE3]/5 transition-colors p-6 text-center cursor-pointer"
        onClick={() => inputRef.current?.click()}
        onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        onDragOver={(e) => e.preventDefault()}
        data-testid="bulk-grant-dropzone"
      >
        <Upload className="mx-auto text-slate-400" size={28} />
        <div className="mt-3 font-medium text-slate-700">Drop a CSV file or click to browse</div>
        <div className="text-xs text-slate-500 mt-1">
          Expected columns: <span className="font-mono">email</span> (required), <span className="font-mono">first_name</span>, <span className="font-mono">last_name</span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0])}
          data-testid="bulk-grant-file-input"
        />
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
          …or paste a list (one row per line, comma-separated)
        </div>
        <textarea
          value={pasteText}
          onChange={(e) => setPasteText(e.target.value)}
          rows={4}
          className="w-full rounded-xl border border-slate-200 py-3 px-3.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/30"
          placeholder={"jane.doe@hospital.org, Jane, Doe\nfred@example.com\n…"}
          data-testid="bulk-grant-paste-input"
        />
        <Button
          size="sm"
          variant="outline"
          onClick={() => { if (pasteText.trim()) { onPaste(pasteText); setPasteText(""); } }}
          disabled={!pasteText.trim()}
          className="mt-2 rounded-full text-xs"
          data-testid="bulk-grant-paste-parse-btn"
        >
          Parse {pasteText.split(/\r?\n/).filter((l) => l.trim()).length || 0} row(s)
        </Button>
      </div>

      {!!rawRows.length && (
        <div className="rounded-2xl bg-emerald-50 ring-1 ring-emerald-200 p-4 flex items-center gap-3" data-testid="bulk-grant-parsed-summary">
          <FileText className="text-emerald-600 shrink-0" size={18} />
          <div className="text-sm text-emerald-900 flex-1">
            <span className="font-semibold">{rawRows.length}</span> row{rawRows.length === 1 ? "" : "s"} ready to preview.
          </div>
          <button
            type="button"
            onClick={onReset}
            className="text-xs text-emerald-700 hover:text-emerald-900 underline"
            data-testid="bulk-grant-reset-btn"
          >
            Clear
          </button>
        </div>
      )}

      <div className="rounded-2xl bg-slate-900 ring-1 ring-white/10 p-4 text-white" data-testid="bulk-grant-test-email-block">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-semibold">Preview the welcome email first</div>
        <div className="text-xs text-white/70 mt-1.5 leading-relaxed">
          Sends a sample of the F2F welcome template to any address (with a fake temp password). No ticket is granted and nothing is written to the database — pure preview.
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 min-w-[180px] rounded-xl bg-white/5 border border-white/10 text-white text-sm py-2 px-3 placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#28BBE3]/40"
            data-testid="bulk-grant-test-email-input"
          />
          <Button
            size="sm"
            onClick={sendTestEmail}
            disabled={sendingTest || !testEmail.trim()}
            className="rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white text-xs"
            data-testid="bulk-grant-test-email-send-btn"
          >
            {sendingTest ? "Sending…" : "Send test"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreviewStep({ preview }) {
  const { rows, summary } = preview;
  return (
    <div className="space-y-4 pt-2" data-testid="bulk-grant-preview-step">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <SummaryStat tone="emerald" icon={CheckCircle2} label="New accounts" value={summary.new} />
        <SummaryStat tone="emerald" icon={CheckCircle2} label="Existing → grant" value={summary.existing_no_ticket} />
        <SummaryStat tone="slate" icon={AlertCircle} label="Already a ticket" value={summary.skipped_existing_ticket + summary.skipped_admin} />
        <SummaryStat tone="rose" icon={XCircle} label="Invalid / dupe" value={summary.invalid + summary.duplicate_in_list} />
      </div>
      <div className="rounded-2xl ring-1 ring-black/5 overflow-hidden bg-white">
        <div className="overflow-y-auto" style={{ maxHeight: 320 }}>
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={i} data-testid={`bulk-row-${i}`}>
                  <td className="px-3 py-2 text-slate-700 font-mono">{r.email}</td>
                  <td className="px-3 py-2 text-slate-600">{r.name || <span className="text-slate-300">—</span>}</td>
                  <td className="px-3 py-2 text-right"><StatusPill status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="text-xs text-slate-500">
        ✔ Each new account gets a welcome email with a temp password and a portal link.
        At ~10 emails/sec this batch will take roughly <span className="font-semibold">{Math.ceil((summary.will_grant || 0) * 0.12)} second{summary.will_grant === 1 ? "" : "s"}</span>.
      </div>
    </div>
  );
}

function RunningStep({ status, totalActionable }) {
  if (!status) {
    return (
      <div className="py-10 text-center text-slate-500 text-sm" data-testid="bulk-grant-running-step">
        <Loader2 className="mx-auto animate-spin text-[#28BBE3]" size={28} />
        <div className="mt-3">Starting job…</div>
      </div>
    );
  }
  const processed = (status.granted || 0) + (status.skipped || 0) + (status.failed || 0);
  const pct = status.total ? Math.round((processed / status.total) * 100) : 0;
  const done = status.status === "done" || status.status === "failed";
  return (
    <div className="space-y-5 pt-2" data-testid="bulk-grant-running-step">
      <div className="flex items-center gap-3">
        {done ? (
          <CheckCircle2 className="text-emerald-500" size={28} />
        ) : (
          <Loader2 className="animate-spin text-[#28BBE3]" size={28} />
        )}
        <div className="flex-1">
          <div className="font-heading text-lg text-slate-900 tracking-tight">
            {done ? "Job complete" : "Sending welcome emails…"}
          </div>
          <div className="text-xs text-slate-500 mt-0.5">
            Processed <span className="font-semibold">{processed}</span> of {status.total}
          </div>
        </div>
      </div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="bulk-grant-progress-bar">
        <div className="h-full bg-[#28BBE3] transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-2 text-xs">
        <SummaryStat tone="emerald" icon={CheckCircle2} label="Granted" value={status.granted || 0} />
        <SummaryStat tone="slate" icon={AlertCircle} label="Skipped" value={status.skipped || 0} />
        <SummaryStat tone="rose" icon={XCircle} label="Failed" value={status.failed || 0} />
      </div>
      {!!(status.errors || []).length && (
        <div className="rounded-2xl bg-rose-50 ring-1 ring-rose-200 p-3 text-xs text-rose-900 max-h-32 overflow-y-auto" data-testid="bulk-grant-errors">
          <div className="font-semibold mb-1">Errors:</div>
          {(status.errors || []).slice(0, 20).map((e, i) => (
            <div key={i} className="font-mono text-[11px]">{e.email}: {e.error}</div>
          ))}
        </div>
      )}
      {!totalActionable && (
        <div className="text-[11px] text-slate-400">Job ID: {status.job_id}</div>
      )}
    </div>
  );
}

function SummaryStat({ icon: Icon, label, value, tone }) {
  const tones = {
    emerald: "bg-emerald-50 ring-emerald-200 text-emerald-700",
    rose: "bg-rose-50 ring-rose-200 text-rose-700",
    slate: "bg-slate-50 ring-slate-200 text-slate-700",
  };
  return (
    <div className={`rounded-xl ring-1 px-3 py-2.5 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] font-semibold opacity-80">
        <Icon size={11} />
        {label}
      </div>
      <div className="mt-0.5 text-lg font-heading tracking-tight">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    "new":                       { label: "Will create + email",    cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    "existing-no-ticket":        { label: "Will grant + email",     cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
    "skipped-existing-ticket":   { label: "Skip · already has",     cls: "bg-slate-50 text-slate-600 ring-slate-200" },
    "skipped-admin":             { label: "Skip · admin",           cls: "bg-slate-50 text-slate-600 ring-slate-200" },
    "invalid":                   { label: "Invalid email",          cls: "bg-rose-50 text-rose-700 ring-rose-200" },
    "duplicate-in-list":         { label: "Duplicate row",          cls: "bg-rose-50 text-rose-700 ring-rose-200" },
  };
  const v = map[status] || { label: status || "—", cls: "bg-slate-50 text-slate-600 ring-slate-200" };
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ring-1 ${v.cls}`}>
      {v.label}
    </span>
  );
}

/* ----------------------------- CSV parsing ------------------------------- */
// Minimal RFC-4180-ish CSV parser. Detects header row by sniffing first row
// for an `email`-like column; otherwise assumes first column is email.
function parseCsv(text) {
  const rows = csvSplit(text.replace(/^\uFEFF/, ""));
  if (!rows.length) return [];

  const first = rows[0].map((c) => c.toLowerCase().trim());
  const looksLikeHeader = first.some((c) => /email|e-mail/.test(c));
  let emailIdx = 0;
  let firstNameIdx = 1;
  let lastNameIdx = 2;
  let nameIdx = -1;
  let data = rows;

  if (looksLikeHeader) {
    emailIdx = first.findIndex((c) => /^e-?mail/.test(c));
    firstNameIdx = first.findIndex((c) => /^first[_\s]?name|^firstname|^given/.test(c));
    lastNameIdx = first.findIndex((c) => /^last[_\s]?name|^lastname|^surname|^family/.test(c));
    nameIdx = first.findIndex((c) => /^(full[_\s]?)?name$/.test(c));
    data = rows.slice(1);
  }

  return data
    .filter((r) => r.length && (r[emailIdx] || "").trim())
    .map((r) => {
      const email = (r[emailIdx] || "").trim();
      let first_name = firstNameIdx >= 0 ? (r[firstNameIdx] || "").trim() : "";
      let last_name = lastNameIdx >= 0 ? (r[lastNameIdx] || "").trim() : "";
      if (!first_name && !last_name && nameIdx >= 0) {
        const full = (r[nameIdx] || "").trim();
        const parts = full.split(/\s+/);
        first_name = parts[0] || "";
        last_name = parts.slice(1).join(" ");
      }
      return { email, first_name, last_name };
    });
}

function csvSplit(text) {
  const rows = [];
  let row = [];
  let cur = "";
  let i = 0;
  let inQuotes = false;
  while (i < text.length) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i += 2; continue; }
        inQuotes = false; i++; continue;
      }
      cur += ch; i++; continue;
    }
    if (ch === '"') { inQuotes = true; i++; continue; }
    if (ch === ",") { row.push(cur); cur = ""; i++; continue; }
    if (ch === "\r") { i++; continue; }
    if (ch === "\n") { row.push(cur); rows.push(row); row = []; cur = ""; i++; continue; }
    cur += ch; i++;
  }
  if (cur || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => (c || "").trim()));
}
